import { motion } from "framer-motion";
import { useState } from "react";

const ContactPage = () => {
    return (
        <main data-testid="contact-page">
            {/* Hero */}
            <section className="relative pt-28 md:pt-36 pb-12 md:pb-16">
                <div className="max-w-[1280px] mx-auto px-6 md:px-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="max-w-4xl"
                    >
                        <span className="eyebrow-pill">Contact</span>
                        <h1 className="font-display text-[clamp(2.4rem,5.4vw,4.4rem)] leading-[1.02] tracking-tight text-[var(--ink)] mt-5 font-semibold">
                            Let&apos;s map your{" "}
                            <span className="accent-blue">operations</span> together.
                        </h1>
                        <p className="mt-6 text-xl text-[var(--ink-soft)] leading-relaxed max-w-2xl">
                            No slides. No pitch deck. Bring whatever&apos;s burying your team — and we&apos;ll quantify the
                            hours Lexafide can reclaim.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Contact methods */}
            <section className="relative pb-20 md:pb-28">
                <div className="max-w-[1280px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-8 lg:gap-12">
                    {/* Left: methods */}
                    <div className="space-y-4">
                        <ContactMethod
                            tag="Fastest"
                            icon="fa-calendar-days"
                            label="Book a 30-min demo"
                            value="A working conversation, not an SDR pitch. Bring whatever's burying your team — we'll show you exactly which agent absorbs it, and how fast it ships."
                            cta="Pick a time"
                            href="https://calendly.com/d/d3nd-zhb-2fr/lexafide-demo-discussion-30-minute-meeting-web"
                            highlighted
                            testid="contact-calendly"
                        />
                        <ContactMethod
                            tag="Direct"
                            icon="fa-envelope"
                            label="Email the founders"
                            value="hello@lexafide.com"
                            cta="Send email"
                            href="mailto:hello@lexafide.com"
                            testid="contact-email"
                        />
                        <ContactMethod
                            tag="Followers"
                            icon="fa-linkedin-in"
                            iconBrand
                            label="Follow on LinkedIn"
                            value="linkedin.com/company/lexafide"
                            cta="View profile"
                            href="https://www.linkedin.com/company/lexafide"
                            testid="contact-linkedin"
                        />

                        {/* What to expect */}
                        <div className="mt-8 rounded-2xl border border-[var(--border)] bg-[var(--cream-2)] p-6">
                            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--ink-dim)] font-semibold mb-3">
                                What happens after you reach out
                            </div>
                            <ol className="space-y-3">
                                {[
                                    { n: "1", title: "30-min discovery", body: "We walk through your firm's workflows, telephony, and CMS together — and the work that's burying your team." },
                                    { n: "2", title: "Tailored agent map", body: "Within a week, you get a concrete map of which Lexafide agents fit and the hours they reclaim." },
                                    { n: "3", title: "Pilot deployment", body: "Live in 14 days on average. Low-cost pilot first — prove value before you scale." },
                                ].map((s) => (
                                    <li key={s.n} className="flex items-start gap-3">
                                        <span className="w-6 h-6 rounded-full bg-[var(--ink)] text-white font-display font-bold text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                                            {s.n}
                                        </span>
                                        <div>
                                            <div className="text-[var(--ink)] font-semibold text-[14px] leading-tight">{s.title}</div>
                                            <div className="text-[var(--ink-soft)] text-[13px] mt-1 leading-snug">{s.body}</div>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>

                    {/* Right: contact form */}
                    <ContactForm />
                </div>
            </section>
        </main>
    );
};

/* ---------- Single contact method ---------- */
const ContactMethod = ({ tag, icon, iconBrand, label, value, cta, href, highlighted, testid }) => (
    <motion.a
        initial={{ opacity: 0, y: 14 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.5 }}
        whileHover={{ y: -2 }}
        href={href}
        target={href.startsWith("http") ? "_blank" : undefined}
        rel={href.startsWith("http") ? "noreferrer" : undefined}
        data-testid={testid}
        className={`group block rounded-2xl border p-5 md:p-6 transition-colors ${
            highlighted
                ? "border-[rgba(30,91,255,0.4)] bg-[var(--blue-soft)]/40 hover:bg-[var(--blue-soft)]"
                : "border-[var(--border-strong)] bg-[var(--paper)] hover:border-[var(--ink)]"
        }`}
    >
        <div className="flex items-center gap-4">
            <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    highlighted ? "bg-[var(--blue)] text-white" : "bg-[var(--cream-2)] text-[var(--ink)]"
                }`}
            >
                <i className={`${iconBrand ? "fa-brands" : "fa-solid"} ${icon} text-base`} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--ink-dim)] font-semibold mb-0.5">{tag}</div>
                <div className="font-display text-[var(--ink)] font-semibold text-[16px] leading-tight">{label}</div>
                <div className="text-[var(--ink-soft)] text-[13px] mt-0.5 leading-snug">{value}</div>
            </div>
            <div className="text-sm text-[var(--ink)] font-medium hidden sm:flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                {cta}
                <i className="fa-solid fa-arrow-right text-[10px]" />
            </div>
        </div>
    </motion.a>
);

/* ---------- Contact form ---------- */
const ContactForm = () => {
    const [status, setStatus] = useState("idle"); // idle | submitting | success | error
    const [errorMsg, setErrorMsg] = useState("");
    const [form, setForm] = useState({ name: "", firm: "", email: "", role: "Intake", volume: "Under 500 calls / mo", message: "" });

    const onSubmit = async (e) => {
        e.preventDefault();
        setStatus("submitting");
        setErrorMsg("");
        try {
            const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            if (!resp.ok) throw new Error(`Server responded ${resp.status}`);
            setStatus("success");
        } catch (err) {
            setStatus("error");
            setErrorMsg("Something went wrong. Please try again or email us directly at hello@lexafide.com.");
        }
    };

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="card-paper p-6 md:p-8"
        >
            <div className="mb-6">
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--ink-dim)] font-semibold mb-2">
                    Or tell us about your firm
                </div>
                <h2 className="font-display text-2xl md:text-3xl font-semibold text-[var(--ink)] leading-tight">
                    We&apos;ll respond within <span className="accent-blue">one business day.</span>
                </h2>
            </div>

            {status === "success" ? (
                <div className="rounded-xl border border-[rgba(34,197,94,0.4)] bg-[rgba(34,197,94,0.06)] p-6 text-center" data-testid="contact-success">
                    <div className="w-12 h-12 rounded-full bg-[#16A34A] mx-auto flex items-center justify-center mb-4">
                        <i className="fa-solid fa-check text-white text-xl" />
                    </div>
                    <div className="font-display font-semibold text-[var(--ink)] text-lg">Thanks — we&apos;ve got it.</div>
                    <p className="text-[var(--ink-soft)] text-sm mt-2 max-w-sm mx-auto">
                        Our team will reach out within one business day. In the meantime,{" "}
                        <a href="https://calendly.com/d/d3nd-zhb-2fr/lexafide-demo-discussion-30-minute-meeting-web" target="_blank" rel="noreferrer" className="accent-blue font-medium link-grow">
                            book a 30-min slot directly
                        </a>.
                    </p>
                </div>
            ) : (
                <form onSubmit={onSubmit} className="space-y-4" data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Your name" required>
                            <input
                                type="text"
                                required
                                data-testid="form-name"
                                value={form.name}
                                onChange={update("name")}
                                placeholder="Maria S."
                                className="form-input"
                            />
                        </Field>
                        <Field label="Firm name" required>
                            <input
                                type="text"
                                required
                                data-testid="form-firm"
                                value={form.firm}
                                onChange={update("firm")}
                                placeholder="Sterling &amp; Vale Law"
                                className="form-input"
                            />
                        </Field>
                    </div>
                    <Field label="Work email" required>
                        <input
                            type="email"
                            required
                            data-testid="form-email"
                            value={form.email}
                            onChange={update("email")}
                            placeholder="m.s@sterlingvale.com"
                            className="form-input"
                        />
                    </Field>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Your role">
                            <select data-testid="form-role" value={form.role} onChange={update("role")} className="form-input">
                                {["Intake", "Paralegal", "Case Manager", "Attorney / Partner", "Operations / Admin", "Other"].map((r) => (
                                    <option key={r}>{r}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Monthly call volume">
                            <select data-testid="form-volume" value={form.volume} onChange={update("volume")} className="form-input">
                                {["Under 500 calls / mo", "500–2,000 calls / mo", "2,000–5,000 calls / mo", "5,000+ calls / mo"].map((v) => (
                                    <option key={v}>{v}</option>
                                ))}
                            </select>
                        </Field>
                    </div>
                    <Field label="What's burying your team?">
                        <textarea
                            data-testid="form-message"
                            value={form.message}
                            onChange={update("message")}
                            rows={4}
                            placeholder="Missed calls after 5pm. Intake forms incomplete. Adjuster chases falling through. Tell us your story."
                            className="form-input resize-none"
                        />
                    </Field>
                    {status === "error" && (
                        <div className="text-[13px] text-[#DC2626]" data-testid="form-error">{errorMsg}</div>
                    )}
                    <button
                        type="submit"
                        disabled={status === "submitting"}
                        data-testid="form-submit"
                        className="btn-primary w-full sm:w-auto inline-flex items-center justify-center gap-2 text-sm disabled:opacity-60"
                    >
                        {status === "submitting" ? "Sending…" : "Send message"}
                        <i className="fa-solid fa-arrow-right text-[10px]" />
                    </button>
                    <p className="text-[11px] font-mono tracking-wider text-[var(--ink-dim)] uppercase">
                        We&apos;ll never share your details · HIPAA-aligned
                    </p>
                </form>
            )}
        </motion.div>
    );
};

const Field = ({ label, required, children }) => (
    <label className="block">
        <span className="block font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--ink-dim)] font-semibold mb-1.5">
            {label}{required && <span className="text-[#DC2626] ml-1">*</span>}
        </span>
        {children}
    </label>
);

export default ContactPage;
